import { useCallback, useMemo } from "react"

import { Logger } from "shared/lib/Logger"

import { endOfDay, subDays } from "date-fns"

import { useProjectHistoryQuery } from "entities/projects/graphql/project-history/queries.generated"

import { ProjectHistoryEntrySchema } from "../types/ProjectHistoryEntry"

/**
 * Mirrors the resource types the history endpoint accepts, so it stays complete
 * even while only PROJECTS has a caller.
 * @public
 */
export enum HistoryResourceType {
  PROJECTS = "projects",
  ROOMS = "rooms",
  FLOORS = "floors",
  PROJECT_POSITIONS = "project-positions"
}

function extractCursor(
  url: string | null | undefined,
  param: "after" | "before"
): string | null {
  if (!url) return null
  const match = url.match(new RegExp(`[?&]${param}=([^&]+)`))
  return match ? decodeURIComponent(match[1]) : null
}

export function useProjectHistory({
  resourceType,
  id,
  lastDays = 30
}: {
  resourceType: HistoryResourceType
  id: string
  lastDays: number
}) {
  const nowISO = endOfDay(new Date()).toISOString()
  const lastDaysISO = endOfDay(subDays(new Date(), lastDays)).toISOString()

  const { data, loading, fetchMore } = useProjectHistoryQuery({
    variables: {
      resourceType,
      id,
      from: lastDaysISO,
      to: nowISO,
      limit: 100
    },
    skip: !id || !resourceType
  })

  const nextCursor = extractCursor(data?.projectHistory?.links?.next, "after")

  const loadMore = useCallback(() => {
    if (!nextCursor) return
    fetchMore({
      variables: { after: nextCursor },
      updateQuery: (prev, { fetchMoreResult }) => {
        return {
          ...prev,
          projectHistory: {
            ...prev.projectHistory,
            entries: [
              ...prev.projectHistory.entries,
              ...fetchMoreResult.projectHistory.entries
            ],
            links: fetchMoreResult.projectHistory.links
          }
        }
      }
    })
  }, [fetchMore, nextCursor])

  const entries = useMemo(() => {
    return Iterator.from(data?.projectHistory?.entries ?? [])
      .map(entry => {
        const parseResult = ProjectHistoryEntrySchema.safeParse(entry)
        if (parseResult.error) {
          Logger.error(
            "InvalidProjectHistoryEntry",
            "project history entry does not match zod schema",
            {
              error: parseResult.error
            }
          )
        }

        return parseResult.success ? entry : null
      })
      .filter(Boolean)
      .toArray()
  }, [data?.projectHistory?.entries])

  return {
    entries,
    isLoading: loading,
    hasMore: !!nextCursor,
    loadMore
  }
}
